'use client'
import { useEffect, useState } from 'react'
import { VStack } from '@chakra-ui/react'
import type {
  CreateInvoiceOrderDTO,
  IngridRecipient,
  OrderAddressDTO,
  ShipmentDTO
} from 'ecosystem'
import { useBuyerShipping } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import { IngridCheckout } from '../../IngridCheckout'
import { subscribeToIngridEvents } from '../../IngridCheckout/utils'
import { BillingAddressPicker, useBillingAddress } from '../DefaultDelivery'
import { PaymentWithDelivery } from '../PaymentWithDelivery'
import { InvoiceReviewPanel } from './InvoiceReviewPanel'
import { useDebouncedDraftSync } from './useDebouncedDraftSync'

const recipientToOrderAddress = (r: IngridRecipient | undefined): OrderAddressDTO | undefined => {
  if (!r) return undefined
  return {
    address1: r.address_lines || `${r.street} ${r.street_number}`.trim(),
    address2: '',
    attention: [r.first_name, r.last_name].filter(Boolean).join(' '),
    country: r.country,
    mobilePhone: r.phone_number,
    postalCode: r.postal_code,
    postalRegion: r.city,
    reference: ''
  }
}

interface InvoiceAndIngridProps {
  submitPath: string
}

export const InvoiceAndIngrid = ({ submitPath }: InvoiceAndIngridProps) => {
  const { orderResponse, invoiceOrderResponse, paymentProviderConfig } = useCheckoutContext()
  const { rebaseShippingQuote } = useBuyerShipping(paymentProviderConfig)
  const [isReady, setIsReady] = useState(false)
  const [shippingCost, setShippingCost] = useState<number | undefined>(undefined)
  const [shippingAddress, setShippingAddress] = useState<OrderAddressDTO | undefined>(undefined)
  const { billingAddress, setBillingAddress } = useBillingAddress(
    invoiceOrderResponse?.storefrontOrderResponse?.billingAddress
  )

  useEffect(() => {
    if (!isReady || orderResponse?.ingridResponse?.session.status !== 'ACTIVE') {
      return
    }

    subscribeToIngridEvents({
      summaryChanged: (summary, meta) => {
        if (meta.total_value_changed) {
          setShippingCost(rebaseShippingQuote(summary.total_value))
        }
        if (meta.delivery_address_changed) {
          setShippingAddress(recipientToOrderAddress(summary.delivery_address))
        }
      }
    })
  }, [isReady, orderResponse?.ingridResponse?.session.status, rebaseShippingQuote])

  const shipment: ShipmentDTO = {
    shippingProvider: 'INGRID',
    ...(orderResponse?.ingridResponse?.session.checkout_session_id
      ? { shipmentId: orderResponse.ingridResponse.session.checkout_session_id }
      : {}),
    ...(shippingAddress ? { address: shippingAddress } : {})
  }

  const draftOverrides: Partial<CreateInvoiceOrderDTO> = {
    ...(shippingCost !== undefined ? { shippingCost } : {}),
    shipments: [shipment],
    ...(billingAddress ? { billingAddress } : {})
  }

  // Submit is blocked until the shopper has picked a delivery option in Ingrid —
  // we treat "shipping cost present" as that signal.
  const isSubmitBlocked = shippingCost === undefined

  const { flush: flushDraftSync } = useDebouncedDraftSync(draftOverrides, !isSubmitBlocked)

  return (
    <PaymentWithDelivery
      deliveryComponent={
        <IngridCheckout
          ingridResponseData={orderResponse?.ingridResponse}
          onReady={() => setIsReady(true)}
        />
      }
      paymentComponent={
        <VStack align="stretch" spacing={4} maxW="640px" w="100%">
          <BillingAddressPicker onChange={setBillingAddress} value={billingAddress} />
          <InvoiceReviewPanel
            draftOverrides={draftOverrides}
            flushDraftSync={flushDraftSync}
            isDisabled={isSubmitBlocked}
            submitPath={submitPath}
          />
        </VStack>
      }
    />
  )
}
